// Shared code snippets for onboarding UI
// Source of truth: packages/docs/content/getting-started/quickstart.mdx

import { queryOptions } from '@tanstack/react-query';
import { highlightCode } from '@/lib/shiki';

export const ENV_SNIPPET = 'MESA_API_KEY=your-api-key-here';

export const SDK_INIT_SNIPPET = `import { Mesa } from "@mesadev/rest";

const mesa = new Mesa({
  apiKey: process.env.MESA_API_KEY
});`;

export function getSdkCommitSnippet(org: string, repo: string): string {
  return `import { Mesa } from "@mesadev/rest";

const mesa = new Mesa({
  apiKey: process.env.MESA_API_KEY
});

const repoInfo = await mesa.repos.get({
  org: "${org}",
  repo: "${repo}",
});

const change = await mesa.changes.create({
  org: "${org}",
  repo: "${repo}",
  base_change_id: repoInfo.head_change_id!,
  message: "Initial change",
  author: { name: "Agent", email: "agent@example.com" },
  files: [
    { path: "README.md", action: "upsert", content: Buffer.from("# ${repo}").toString("base64"), encoding: "base64" }
  ]
});

await mesa.bookmarks.move({
  org: "${org}",
  repo: "${repo}",
  bookmark: "main",
  change_id: change.id,
});`;
}

export const SDK_INSTALL_SNIPPET = 'npm install @mesadev/rest';

// Module-level cache: these snippets are static strings, so Shiki only needs to
// run once per Lambda instance instead of on every request.
let cachedSnippets: { installSnippetHtml: string; sdkSnippetHtml: string } | null = null;

export const repoSnippetsQueryOptions = () =>
  queryOptions({
    queryKey: ['repo-snippets'],
    staleTime: Number.POSITIVE_INFINITY,
    queryFn: async () => {
      if (cachedSnippets) return cachedSnippets;
      const [installSnippetHtml, sdkSnippetHtml] = await Promise.all([
        highlightCode(SDK_INSTALL_SNIPPET, 'bash'),
        highlightCode(getSdkCommitSnippet('ORG', 'REPO'), 'typescript'),
      ]);
      cachedSnippets = { installSnippetHtml, sdkSnippetHtml };
      return cachedSnippets;
    },
  });
